'use client';

import { useId, type Dispatch, type ReactNode } from 'react';
import { SkLink } from '../shared/primitives';
import { NumberField } from '../doors/NumberField';
import {
  BAY_KIND_LABEL,
  bayGeometry,
  frontsFor,
  insideHeight,
  limitsFor,
  RULES,
  type BayKind,
  type Cabinet,
  type CabinetType,
  type CornerKind,
} from './model';
import type { CabinetOrderAction, SizeField } from './orderState';

/**
 * The made-to-measure panels for the cabinet open in the editor: its type, its
 * sizes (and an under-stairs run's bays), and its inside: doors, drawer fronts
 * and shelves, each to the millimetre with a way back to standard.
 */
const TYPES: ReadonlyArray<{ type: CabinetType; label: string; hint: string }> = [
  { type: 'base', label: 'Base', hint: 'Under the worktop' },
  { type: 'wall', label: 'Wall', hint: 'Hung on the wall' },
  { type: 'tall', label: 'Tall', hint: 'Larder or housing' },
  { type: 'drawers', label: 'Drawers', hint: 'A drawer stack' },
  { type: 'corner', label: 'Corner', hint: 'L, blind or angled' },
  { type: 'wardrobe', label: 'Wardrobe', hint: 'Bedroom carcass' },
  { type: 'stairs', label: 'Under stairs', hint: 'Cut to the slope' },
];

const CORNERS: ReadonlyArray<[CornerKind, string]> = [
  ['l', 'L-shaped'],
  ['blind', 'Blind'],
  ['diag', 'Diagonal wall'],
];

const BAY_KINDS = Object.entries(BAY_KIND_LABEL) as Array<[BayKind, string]>;

interface Props {
  cabinet: Cabinet;
  index: number;
  dispatch: Dispatch<CabinetOrderAction>;
}

function Step({ n, title, aside }: { n: number; title: string; aside?: ReactNode }) {
  return (
    <div className="sk-db__step">
      <span className="sk-db__n" aria-hidden="true">
        {n}
      </span>
      <h2 className="sk-heading">{title}</h2>
      {aside && <span className="sk-db__aside">{aside}</span>}
    </div>
  );
}

export function TypePanel({ cabinet: c, dispatch }: Props) {
  return (
    <div className="sk-db__panel">
      <Step n={1} title="Cabinet type" />
      <div className="sk-cb__types" role="group" aria-label="Cabinet type">
        {TYPES.map((t) => (
          <button key={t.type} type="button" aria-pressed={c.type === t.type} onClick={() => dispatch({ type: 'cabinetType', cabinetType: t.type })}>
            <strong>{t.label}</strong>
            <span>{t.hint}</span>
          </button>
        ))}
      </div>
      {c.type === 'corner' && (
        <div className="sk-cb__sub">
          <div className="sk-seg" role="group" aria-label="Corner type">
            {CORNERS.map(([corner, label]) => (
              <button key={corner} type="button" aria-pressed={c.corner === corner} onClick={() => dispatch({ type: 'corner', corner })}>
                {label}
              </button>
            ))}
          </div>
          {c.corner === 'blind' && (
            <div className="sk-seg" role="group" aria-label="Blind side">
              {(['left', 'right'] as const).map((side) => (
                <button key={side} type="button" aria-pressed={c.blind === side} onClick={() => dispatch({ type: 'blind', side })}>
                  {side === 'left' ? 'Blind on the left' : 'Blind on the right'}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {c.type === 'stairs' && (
        <p className="sk-cb__note">
          A run of bays under the stairs, each its own pull-out, door or drawer stack, with the fronts cut to the slope. Measure the
          height at both ends of the run.
        </p>
      )}
    </div>
  );
}

export function SizePanel({ cabinet: c, dispatch, measureHref }: Props & { measureHref?: string }) {
  const id = useId();
  const l = limitsFor(c.type, c.corner);
  const stairs = c.type === 'stairs';
  const lShape = c.type === 'corner' && c.corner === 'l';
  const field = (f: SizeField, label: string, range: string) => (
    <div className="sk-db__field" key={f}>
      <label className="sk-db__label" htmlFor={`${id}-${f}`}>
        {label}
      </label>
      <NumberField id={`${id}-${f}`} value={c[f]} suffix="mm" onCommit={(value) => dispatch({ type: 'size', field: f, value })} />
      <span className="sk-db__hint" style={{ margin: 0 }}>
        {range}
      </span>
    </div>
  );
  return (
    <div className="sk-db__panel">
      <Step
        n={2}
        title="Sizes"
        aside={
          measureHref ? (
            <SkLink href={measureHref} className="sk-link-arrow">
              How to measure
            </SkLink>
          ) : (
            'All sizes in millimetres'
          )
        }
      />
      <div className="sk-cb__sizes">
        {field('w', stairs ? 'Length under the stairs' : lShape || (c.type === 'corner' && c.corner === 'diag') ? 'Width along this wall' : 'Width', `${l.w[0]}–${l.w[1]}`)}
        {lShape && field('b', 'Width along the other wall', '800–1200')}
        {c.type === 'corner' && c.corner === 'blind' && field('blank', 'Blank section', `300–${Math.max(300, c.w - 300)}`)}
        {field('h', stairs ? 'Height at the tall end' : 'Height', `${l.h[0]}–${l.h[1]}`)}
        {stairs && field('h2', 'Height at the low end', `200–${c.h - 50}`)}
        {field('d', 'Depth', `${l.d[0]}–${l.d[1]}`)}
        <div className="sk-db__field">
          <label className="sk-db__label" htmlFor={`${id}-qty`}>
            How many
          </label>
          <NumberField id={`${id}-qty`} value={c.qty} min={1} max={99} onCommit={(value) => dispatch({ type: 'qty', value })} />
        </div>
      </div>
      {c.type !== 'wall' && c.type !== 'stairs' && !(c.type === 'corner' && c.corner === 'diag') && (
        <p className="sk-cb__note">{`Height is the carcass alone. It stands on ${RULES.plinth}mm legs, hidden by a plinth.`}</p>
      )}
      {stairs && <BaysTable cabinet={c} dispatch={dispatch} index={0} />}
    </div>
  );
}

function BaysTable({ cabinet: c, dispatch }: Props) {
  const geo = bayGeometry(c);
  const total = c.bays.reduce((s, b) => s + b.w, 0);
  const diff = c.w - total;
  return (
    <>
      <table className="sk-cb__bays">
        <caption className="sk-visually-hidden">Bays along the run, from the tall end</caption>
        <thead>
          <tr>
            <th scope="col">Bay</th>
            <th scope="col">Type</th>
            <th scope="col">Width</th>
            <th scope="col">Drawers</th>
            <th scope="col">Height</th>
            <th scope="col">
              <span className="sk-visually-hidden">Remove</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {geo.map((bay, at) => {
            const low = bay.kind === 'drawers' && bay.lo / Math.max(1, bay.n ?? 1) < RULES.minDrawerFront;
            return (
              <tr key={at}>
                <td>
                  <b>{at + 1}</b>
                </td>
                <td>
                  <select aria-label={`Bay ${at + 1} type`} value={bay.kind} onChange={(e) => dispatch({ type: 'bayKind', at, kind: e.target.value as BayKind })}>
                    {BAY_KINDS.map(([kind, label]) => (
                      <option key={kind} value={kind}>
                        {label}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <NumberField aria-label={`Bay ${at + 1} width in millimetres`} value={bay.w} suffix="mm" onCommit={(value) => dispatch({ type: 'bayWidth', at, value })} />
                </td>
                <td>
                  {bay.kind === 'drawers' ? (
                    <select aria-label={`Bay ${at + 1} drawers`} value={bay.n ?? 1} onChange={(e) => dispatch({ type: 'bayDrawers', at, count: Number(e.target.value) })}>
                      {[1, 2, 3, 4, 5].map((count) => (
                        <option key={count} value={count}>
                          {count}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span aria-hidden="true">—</span>
                  )}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {`${Math.round(bay.hi)}→${Math.round(bay.lo)}`}
                  {low && <span className="sk-cb__low"> too low</span>}
                </td>
                <td>
                  <button type="button" className="sk-db__del" aria-label={`Remove bay ${at + 1}`} disabled={c.bays.length <= 1} onClick={() => dispatch({ type: 'bayRemove', at })}>
                    ×
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="sk-cb__bayfoot">
        <span aria-live="polite">
          {diff === 0 ? (
            <b className="sk-db__ok">{`${total} of ${c.w} mm filled`}</b>
          ) : diff > 0 ? (
            <b style={{ color: 'var(--sk-brass-text)' }}>{`${diff} mm left to fill`}</b>
          ) : (
            <b className="sk-cb__low">{`${-diff} mm too long`}</b>
          )}
        </span>
        <span style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="sk-btn sk-btn--secondary sk-btn--sm" onClick={() => dispatch({ type: 'baysShare' })}>
            Share the run evenly
          </button>
          <button type="button" className="sk-btn sk-btn--secondary sk-btn--sm" disabled={diff < 150} onClick={() => dispatch({ type: 'bayAdd' })}>
            Add a bay
          </button>
        </span>
      </div>
    </>
  );
}

export function InsidePanel({ cabinet: c, dispatch }: Props) {
  const id = useId();
  const doorsChoice = c.fronts && (c.type === 'base' || c.type === 'wall' || c.type === 'tall' || c.type === 'wardrobe');
  const hingeChoice = c.fronts && (doorsChoice ? c.doors === 1 : c.type === 'corner' && c.corner === 'diag');
  const shelves = c.type !== 'drawers' && c.type !== 'stairs';
  const fronts = frontsFor(c);
  return (
    <div className="sk-db__panel">
      <Step n={3} title="Fronts and inside" />
      <div className="sk-db__toggle">
        <div>
          <strong id={`${id}-fronts`}>Include doors and drawer fronts</strong>
          <span>Off buys the carcass only, for fronts you already have</span>
        </div>
        <span className="sk-db__switch">
          <input type="checkbox" role="switch" aria-labelledby={`${id}-fronts`} checked={c.fronts} onChange={(e) => dispatch({ type: 'fronts', on: e.target.checked })} />
          <i />
        </span>
      </div>

      {doorsChoice && (
        <div className="sk-cb__sub">
          <span className="sk-db__label" id={`${id}-doors`}>
            Doors
          </span>
          <div className="sk-seg" role="group" aria-labelledby={`${id}-doors`}>
            {[1, 2].map((count) => (
              <button key={count} type="button" aria-pressed={c.doors === count} onClick={() => dispatch({ type: 'doors', count })}>
                {count === 1 ? 'One door' : 'Two doors'}
              </button>
            ))}
          </div>
          {c.type === 'tall' && (
            <>
              <span className="sk-db__label" id={`${id}-layout`}>
                Door layout
              </span>
              <div className="sk-seg" role="group" aria-labelledby={`${id}-layout`}>
                <button type="button" aria-pressed={(c.upper ?? 0) > 0} onClick={() => dispatch({ type: 'size', field: 'upper', value: (c.upper ?? 0) > 0 ? c.upper : 715 })}>
                  Upper and lower doors
                </button>
                <button type="button" aria-pressed={!((c.upper ?? 0) > 0)} onClick={() => dispatch({ type: 'size', field: 'upper', value: 0 })}>
                  Full-height doors
                </button>
              </div>
              {(c.upper ?? 0) > 0 && (
                <div className="sk-db__field" style={{ maxWidth: 220 }}>
                  <label className="sk-db__label" htmlFor={`${id}-upper`}>
                    Upper door height
                  </label>
                  <NumberField id={`${id}-upper`} value={c.upper} suffix="mm" onCommit={(value) => dispatch({ type: 'size', field: 'upper', value })} />
                </div>
              )}
            </>
          )}
          {hingeChoice && (
            <div className="sk-seg" role="group" aria-label="Hinged on">
              {(['left', 'right'] as const).map((side) => (
                <button key={side} type="button" aria-pressed={c.hinge === side} onClick={() => dispatch({ type: 'hinge', side })}>
                  {side === 'left' ? 'Hinged left' : 'Hinged right'}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {c.type === 'drawers' && (
        <>
          <div className="sk-cb__sub">
            <label className="sk-db__label" htmlFor={`${id}-dc`}>
              Drawers
            </label>
            <select id={`${id}-dc`} className="sk-cb__select" style={{ width: 'auto' }} value={c.drawerFronts.length} onChange={(e) => dispatch({ type: 'drawerCount', count: Number(e.target.value) })}>
              {[1, 2, 3, 4, 5].map((count) => (
                <option key={count} value={count}>
                  {count}
                </option>
              ))}
            </select>
            <button type="button" className="sk-btn sk-btn--secondary sk-btn--sm" onClick={() => dispatch({ type: 'drawersReset' })}>
              {c.drawersCustom ? 'Reset to standard' : 'Standard heights ✓'}
            </button>
          </div>
          <ol className="sk-cb__positions" aria-label="Drawer front heights, top to bottom">
            {c.drawerFronts.map((h, at) => (
              <li key={at} className="sk-db__field">
                <label className="sk-db__label" htmlFor={`${id}-df-${at}`}>{`Drawer ${at + 1} front`}</label>
                <NumberField id={`${id}-df-${at}`} value={h} suffix="mm" onCommit={(value) => dispatch({ type: 'drawerFront', at, value })} />
              </li>
            ))}
          </ol>
        </>
      )}

      {shelves && (
        <>
          <div className="sk-cb__sub">
            <label className="sk-db__label" htmlFor={`${id}-sc`}>
              Shelves
            </label>
            <select id={`${id}-sc`} className="sk-cb__select" style={{ width: 'auto' }} value={c.shelves.length} onChange={(e) => dispatch({ type: 'shelfCount', count: Number(e.target.value) })}>
              {[0, 1, 2, 3, 4, 5, 6].map((count) => (
                <option key={count} value={count}>
                  {count}
                </option>
              ))}
            </select>
            {c.shelves.length > 0 && (
              <button type="button" className="sk-btn sk-btn--secondary sk-btn--sm" onClick={() => dispatch({ type: 'shelvesReset' })}>
                {c.shelvesCustom ? 'Space evenly' : 'Evenly spaced ✓'}
              </button>
            )}
          </div>
          {c.shelves.length > 0 && (
            <ol className="sk-cb__positions" aria-label="Shelf heights from the inside bottom">
              {c.shelves.map((s, at) => (
                <li key={at} className="sk-db__field">
                  <label className="sk-db__label" htmlFor={`${id}-sh-${at}`}>{`Shelf ${at + 1}`}</label>
                  <NumberField id={`${id}-sh-${at}`} value={s} suffix="mm" onCommit={(value) => dispatch({ type: 'shelf', at, value })} />
                </li>
              ))}
            </ol>
          )}
          {c.shelves.length > 0 && <p className="sk-db__hint">{`Measured from the inside bottom; ${insideHeight(c)}mm inside.`}</p>}
        </>
      )}

      {fronts.length > 0 && (
        <table className="sk-cb__fronts">
          <caption className="sk-db__label" style={{ textAlign: 'left' }}>
            Fronts made for this cabinet
          </caption>
          <thead>
            <tr>
              <th scope="col">Front</th>
              <th scope="col">Height × width</th>
              <th scope="col">Qty</th>
            </tr>
          </thead>
          <tbody>
            {fronts.map((f, i) => (
              <tr key={i}>
                <td>{f.where}</td>
                <td>{f.hLow === undefined ? `${f.h} × ${f.w} mm` : `${f.h}→${f.hLow} × ${f.w} mm, cut to the slope`}</td>
                <td>{f.qty * c.qty}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function CarcassChoices({ cabinet: c, dispatch }: Props) {
  const id = useId();
  return (
    <div className="sk-cb__sub" style={{ marginTop: '1.25rem' }}>
      <span className="sk-db__label" id={`${id}-build`}>
        Delivered
      </span>
      <div className="sk-seg" role="group" aria-labelledby={`${id}-build`}>
        <button type="button" aria-pressed={c.build === 'rigid'} onClick={() => dispatch({ type: 'build', build: 'rigid' })}>
          Rigid, ready built
        </button>
        <button type="button" aria-pressed={c.build === 'flat'} onClick={() => dispatch({ type: 'build', build: 'flat' })}>
          Flat-pack
        </button>
      </div>
    </div>
  );
}
